import React, { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const VDAmbientEditor = () => {
  const LANGS = ["En", "Ar", "Ku"];

  const [form, setForm] = useState({
    TopTextEn: "",
    TopTextAr: "",
    TopTextKu: "",

    Modes: [],   // { ModeId, LabelEn, LabelAr, LabelKu }
    Cards: []    // { CardId, TitleEn, TitleAr, TitleKu, DescEn, DescAr, DescKu }
  });

  const [images, setImages] = useState({
    modes: {},   // icon per mode
    cards: {}    // image per card
  });

  const [loading, setLoading] = useState(true);

  // MODAL
  const [showModal, setShowModal] = useState(false);
  const [uploadType, setUploadType] = useState(null); // "mode" or "card"
  const [uploadId, setUploadId] = useState(null);     // ModeId or CardId
  const [uploadFile, setUploadFile] = useState(null);

  const resolve = (u) =>
    u?.startsWith("http") ? u : `${API_BASE}${u}`;

  // ============================
  // LOAD DATA (EN / AR / KU)
  // ============================
  useEffect(() => {
    const load = async () => {
      try {
        const [en, ar, ku] = await Promise.all([
          axios.get(`${API_BASE}/api/VDAmbientSection?lang=en`),
          axios.get(`${API_BASE}/api/VDAmbientSection?lang=ar`),
          axios.get(`${API_BASE}/api/VDAmbientSection?lang=ku`)
        ]);

        // Build modes
        const modes = en.data.modes.map((m, i) => ({
          ModeId: m.id,
          LabelEn: m.label,
          LabelAr: ar.data.modes[i].label,
          LabelKu: ku.data.modes[i].label
        }));

        // Build cards
        const cards = en.data.cards.map((c, i) => ({
          CardId: c.id,
          TitleEn: c.title,
          TitleAr: ar.data.cards[i].title,
          TitleKu: ku.data.cards[i].title,
          DescEn: c.desc,
          DescAr: ar.data.cards[i].desc,
          DescKu: ku.data.cards[i].desc
        }));

        setForm({
          TopTextEn: en.data.topText,
          TopTextAr: ar.data.topText,
          TopTextKu: ku.data.topText,

          Modes: modes,
          Cards: cards
        });

        // IMAGES
        setImages({
          modes: en.data.modes.reduce((acc, m) => {
            acc[m.id] = m.icon;
            return acc;
          }, {}),

          cards: en.data.cards.reduce((acc, c) => {
            acc[c.id] = c.image;
            return acc;
          }, {})
        });

        setLoading(false);
      } catch (err) {
        console.error("FAILED LOADING VDAmbient:", err);
      }
    };

    load();
  }, []);

  // ============================
  // HANDLE TEXT CHANGE
  // ============================
  const handleChange = (e, idx, field, type = "root") => {
    if (type === "root") {
      setForm({ ...form, [e.target.name]: e.target.value });
      return;
    }

    if (type === "mode") {
      const upd = [...form.Modes];
      upd[idx][field] = e.target.value;
      setForm({ ...form, Modes: upd });
    }

    if (type === "card") {
      const upd = [...form.Cards];
      upd[idx][field] = e.target.value;
      setForm({ ...form, Cards: upd });
    }
  };

  // ============================
  // SAVE TEXT
  // ============================
  const handleSave = async () => {
    const token = localStorage.getItem("token");

    try {
      await axios.put(`${API_BASE}/api/VDAmbientSection`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert("Saved successfully!");
    } catch (err) {
      console.error("SAVE FAILED", err);
      alert("Failed to save!");
    }
  };

  // ============================
  // UPLOAD IMAGE
  // ============================
  const handleUpload = async () => {
    if (!uploadFile) return alert("Select a file first.");

    const token = localStorage.getItem("token");
    const fd = new FormData();

    if (uploadType === "mode") {
      fd.append("ModeId", uploadId);
      fd.append("ModeIcon", uploadFile);
    }

    if (uploadType === "card") {
      fd.append("CardId", uploadId);
      fd.append("CardImage", uploadFile);
    }

    try {
      await axios.post(`${API_BASE}/api/VDAmbientSection/media`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      alert("Uploaded!");
      window.location.reload();
    } catch (err) {
      console.error("UPLOAD FAILED", err);
    }
  };

  if (loading) return <p>Loading...</p>;

  // ============================
  // RENDER
  // ============================
  return (
    <div className="container py-4">
      <h2 className="fw-bold mb-4">VOYAH DREAM – Ambient Section Editor</h2>

      {/* ============================
          TOP TEXT (3 LANGS)
      ============================ */}
      <div className="row mb-4">
        {LANGS.map((lang) => (
          <div key={lang} className="col-md-4">
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-body">
                <h5 className="text-primary">{lang}</h5>

                <label className="form-label mt-2">Top Text</label>
                <textarea
                  rows={5}
                  className="form-control"
                  name={`TopText${lang}`}
                  value={form[`TopText${lang}`]}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ============================
          MODES
      ============================ */}
      <h4 className="fw-bold mt-4">Driving Modes</h4>

      {form.Modes.map((m, idx) => (
        <div key={m.ModeId} className="card shadow-sm border-0 mb-4 p-3">
          <h6 className="fw-bold">Mode {idx + 1}</h6>

          <div className="row">
            {LANGS.map((lang) => (
              <div key={lang} className="col-md-4">
                <label className="form-label">Label ({lang})</label>
                <input
                  className="form-control"
                  value={m[`Label${lang}`]}
                  onChange={(e) =>
                    handleChange(e, idx, `Label${lang}`, "mode")
                  }
                />
              </div>
            ))}
          </div>

          <div className="mt-3">
            <img
              src={resolve(images.modes[m.ModeId])}
              alt=""
              className="rounded mb-2"
              style={{ height: 60 }}
            />

            <button
              className="btn btn-primary"
              onClick={() => {
                setUploadType("mode");
                setUploadId(m.ModeId);
                setUploadFile(null);
                setShowModal(true);
              }}
            >
              Upload Icon
            </button>
          </div>
        </div>
      ))}

      {/* ============================
          CARDS
      ============================ */}
      <h4 className="fw-bold mt-4">Cards</h4>

      {form.Cards.map((c, idx) => (
        <div key={c.CardId} className="card shadow-sm border-0 mb-4 p-3">
          <h6 className="fw-bold">Card {idx + 1}</h6>

          <div className="row">
            {LANGS.map((lang) => (
              <div key={lang} className="col-md-6 mb-3">
                <label className="form-label">Title ({lang})</label>
                <input
                  className="form-control"
                  value={c[`Title${lang}`]}
                  onChange={(e) =>
                    handleChange(e, idx, `Title${lang}`, "card")
                  }
                />

                <label className="form-label mt-2">
                  Description ({lang})
                </label>
                <textarea
                  rows={3}
                  className="form-control"
                  value={c[`Desc${lang}`]}
                  onChange={(e) =>
                    handleChange(e, idx, `Desc${lang}`, "card")
                  }
                />
              </div>
            ))}
          </div>

          <img
            src={resolve(images.cards[c.CardId])}
            className="rounded mb-2"
            style={{ width: "100%", maxHeight: 220, objectFit: "cover" }}
            alt=""
          />

          <button
            className="btn btn-primary"
            onClick={() => {
              setUploadType("card");
              setUploadId(c.CardId);
              setUploadFile(null);
              setShowModal(true);
            }}
          >
            Upload Image
          </button>
        </div>
      ))}

      {/* ============================
          SAVE BUTTON
      ============================ */}
      <div className="text-end mb-4">
        <button className="btn btn-success px-4" onClick={handleSave}>
          Save Changes
        </button>
      </div>

      {/* ============================
          MODAL
      ============================ */}
      {showModal && (
        <>
          <div className="modal fade show" style={{ display: "block" }}>
            <div className="modal-dialog">
              <div className="modal-content shadow">
                <div className="modal-header">
                  <h5 className="modal-title">Upload File</h5>
                  <button
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                  />
                </div>

                <div className="modal-body">
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                  />
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={handleUpload}>
                    Upload
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default VDAmbientEditor;
